import { Component, inject, linkedSignal, Pipe, PipeTransform, ChangeDetectionStrategy } from "@angular/core";
import { AsinSeedTypeEnum, Marketplace, MatchType } from "@front/m19-api-client";
import { AsinService } from "@front/m19-services";
import {
  IBadgeComponent,
  IButtonDirective,
  ISelectMenuImports,
  ISwitchButtonComponent,
  MODAL_DATA,
  ModalRef,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { ProductViewComponent } from "@m19-board/product-view/product-view.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

export enum SeedType {
  Keyword = "Keyword",
  Product = "Product",
}

const AsinSeedTypeLabels: Record<AsinSeedTypeEnum, string> = {
  [AsinSeedTypeEnum.WHITELIST]: "Seed",
  [AsinSeedTypeEnum.BLACKLIST]: "Blacklist",
};

@Pipe({ name: "asinSeedTypeLabel", standalone: true })
export class AsinSeedTypeLabelPipe implements PipeTransform {
  transform(type: AsinSeedTypeEnum | undefined | null): string {
    return type ? AsinSeedTypeLabels[type] : "";
  }
}

@UntilDestroy()
@Component({
  selector: "app-add-to-seeds-modal",
  templateUrl: "./add-to-seeds-modal.component.html",
  imports: [
    TranslocoDirective,
    ProductViewComponent,
    ISelectMenuImports,
    IButtonDirective,
    IBadgeComponent,
    ISwitchButtonComponent,
    AsinSeedTypeLabelPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddToSeedsModalComponent {
  private asinService = inject(AsinService);
  private toastrService = inject(ToasterService);
  private translocoService = inject(TranslocoService);

  protected readonly modalData = inject(MODAL_DATA) as AddToSeedsModalData;
  protected readonly modalRef = inject(ModalRef);

  matchType = MatchType.exact;

  seedTypeKeys = {
    [SeedType.Keyword]: "common.keyword",
    [SeedType.Product]: "common.product",
  };
  readonly SeedType = SeedType;
  readonly MatchType = MatchType;
  readonly AsinSeedTypeEnum = AsinSeedTypeEnum;

  protected readonly seedTypes = [AsinSeedTypeEnum.WHITELIST, AsinSeedTypeEnum.BLACKLIST] as const;
  protected readonly asinSeedType = linkedSignal<AsinSeedTypeEnum>(() => this.seedTypes[0]);

  close(): void {
    this.modalRef.close();
  }

  setMatchType(matchType: MatchType) {
    this.matchType = matchType;
  }

  onAsinSeedTypeChange(asinSeedType: AsinSeedTypeEnum | undefined) {
    if (!asinSeedType) return;
    this.asinSeedType.set(asinSeedType);
    if (this.modalData.seedType == SeedType.Keyword) {
      this.setMatchType(MatchType.exact);
    }
  }

  save(): void {
    this.asinService
      .addSeedToAsin(this.modalData.accountId, this.modalData.marketplace, this.modalData.asin, {
        accountId: this.modalData.accountId,
        marketplace: this.modalData.marketplace,
        asin: this.modalData.asin,
        matchType: this.modalData.seedType == SeedType.Keyword ? this.matchType : MatchType.asinSameAs,
        targetingValue: this.modalData.targetingItemValue,
        type: this.asinSeedType(),
      })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("add-to-seeds-modal.asin_this_asin_seeds_successfully_updated", [
              this.modalData.asin,
            ]),
            this.translocoService.translate("add-to-seeds-modal.product_seeds_added"),
          );
          this.close();
        },
        error: (err) => {
          this.toastrService.error(err, this.translocoService.translate("add-to-seeds-modal.cannot_add_product_seeds"));
          this.close();
        },
      });
  }
}

export interface AddToSeedsModalData {
  seedType: SeedType;
  asin: string;
  targetingItemValue: string;
  accountId: string;
  marketplace: Marketplace;
}
